import { useCallback, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Tab,
  TablePagination,
  Tabs,
  TextField,
} from '@mui/material';
import {
  JOB_SOURCES,
  JOB_STATES,
  type Job,
  type JobsPage as JobsResult,
  type JobsQuery,
} from '../../../api/src/jobs/jobs.types';
import { api } from '../api/client';
import { sectionById } from '../api/generated/section-catalog';
import { PageHeader } from '../components/PageHeader';
import { Box, Stack, Typography } from '../components/mui-system';
import { useWorkspace } from '../workspace-context';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { QueueAdmissionStatus } from '../components/QueueAdmissionStatus';

const date = (v: string | null) => (v ? new Date(v).toLocaleString('ru-RU') : 'нет сведений');
const paginationLabels = {
  labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
    count === -1 ? `${from}–${to} · общее число недоступно` : `${from}–${to} из ${count}`,
  getItemAriaLabel: (type: 'first' | 'last' | 'next' | 'previous') =>
    ({
      first: 'Первая страница',
      last: 'Последняя страница',
      next: 'Следующая страница',
      previous: 'Предыдущая страница',
    })[type],
};
const tone = (state: Job['state']) =>
  state === 'failed'
    ? 'error'
    : ['partial', 'unknown'].includes(state)
      ? 'warning'
      : state === 'complete'
        ? 'success'
        : 'default';

function JobSummary({ job }: { job: Job }) {
  return (
    <Stack gap={0.7}>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Typography fontWeight={700}>{job.title}</Typography>
        <Chip size="small" color={tone(job.state)} label={JOB_STATES[job.state]} />
      </Stack>
      <Typography fontSize={13} color="text.secondary">
        {JOB_SOURCES[job.source]}
        {job.stage ? ' · ' + job.stage : ''}
      </Typography>
      {job.total !== null && job.processed !== null && job.total > 0 && (
        <Stack direction="row" gap={1} alignItems="center">
          <CircularProgress
            size={22}
            variant="determinate"
            value={Math.min(100, (100 * job.processed) / job.total)}
            aria-label="Прогресс работы"
          />
          <Typography fontSize={13}>
            Обработано {job.processed} из {job.total}
            {job.failed ? ` · ошибок: ${job.failed}` : ''}
          </Typography>
        </Stack>
      )}
      <Typography fontSize={12} color="text.secondary">
        {job.createdAt ? 'Создано ' + date(job.createdAt) : 'Непрерывный процесс'}
        {job.startedAt ? ' · начало ' + date(job.startedAt) : ''}
        {job.completedAt ? ' · завершено ' + date(job.completedAt) : ''}
        {job.dueAt ? ' · плановое время ' + date(job.dueAt) : ''}
        {job.attempts !== null
          ? ` · попыток: ${job.attempts}${job.maxAttempts ? '/' + job.maxAttempts : ''}`
          : ''}
      </Typography>
      {job.error && (
        <Alert severity={job.state === 'failed' ? 'error' : 'warning'}>{job.error}</Alert>
      )}
    </Stack>
  );
}

function JobDialog({
  workspaceId,
  jobKey,
  system,
  admin,
  onClose,
  onOpen,
}: {
  workspaceId: string;
  jobKey: string;
  system: boolean;
  admin: boolean;
  onClose(): void;
  onOpen(key: string): void;
}) {
  const [page, setPage] = useState(0);
  const load = useCallback(
    () => api.job(workspaceId, jobKey, page + 1, system),
    [workspaceId, jobKey, page, system],
  );
  const { data, error } = useVisiblePolling(load);
  const parent =
    jobKey.startsWith('fakes:') && jobKey.split(':').length === 3
      ? jobKey.split(':').slice(0, 2).join(':')
      : null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Подробности работы</DialogTitle>
      <DialogContent>
        {parent && <Button onClick={() => onOpen(parent)}>К общему запуску</Button>}
        {error && (
          <Alert severity="warning">
            Не удалось обновить: {error}. {data ? 'Показаны последние полученные сведения.' : ''}
          </Alert>
        )}
        {!data && !error && <LinearProgress />}
        {data && (
          <Stack gap={2}>
            {data.queueAdmission && (
              <QueueAdmissionStatus
                profilesEnabled={admin}
                value={error ? { ...data.queueAdmission, freshness: 'stale' } : data.queueAdmission}
                budget={
                  error && data.resourceBudget
                    ? { ...data.resourceBudget, freshness: 'stale' }
                    : data.resourceBudget
                }
              />
            )}
            <JobSummary job={data.job} />
            <Typography fontSize={12}>Статус получен {date(data.observedAt)}</Typography>
            {data.items.map((job) => (
              <Card variant="outlined" key={job.key} sx={{ p: 2 }}>
                <JobSummary job={job} />
                {job.children && <Button onClick={() => onOpen(job.key)}>Открыть группу</Button>}
              </Card>
            ))}
            {data.total > 0 && (
              <TablePagination
                {...paginationLabels}
                component="div"
                count={data.total}
                page={page}
                rowsPerPage={25}
                rowsPerPageOptions={[25]}
                onPageChange={(_, p) => setPage(p)}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {data && (
          <Button component={Link} to={data.job.href}>
            Открыть раздел
          </Button>
        )}
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}

export function JobsPage() {
  const { activeWorkspace, user } = useWorkspace();
  return (
    <JobsWorkspace
      key={activeWorkspace!.id + ':' + user.role}
      workspaceId={activeWorkspace!.id}
      admin={user.isAdmin}
    />
  );
}
function JobsWorkspace({ workspaceId, admin }: { workspaceId: string; admin: boolean }) {
  const [params, setParams] = useSearchParams();
  const system = admin && params.get('scope') === 'system';
  const view = ['active', 'attention', 'history'].includes(params.get('view') ?? '')
    ? (params.get('view') as JobsQuery['view'])
    : 'active';
  const source = params.get('source') ?? '',
    state = params.get('state') ?? '';
  const page = Math.min(9999, Math.max(0, Number(params.get('page')) || 0));
  const period = params.get('period') === 'all' ? 'all' : '30';
  const jobKey = params.get('job');
  const lastPage = useRef<{ key: string; data: JobsResult } | null>(null);
  const load = useCallback(async () => {
    const key = JSON.stringify([workspaceId, system, view, source, state, page, period]);
    const next = await api.jobs(
      workspaceId,
      {
        view,
        source: source in JOB_SOURCES ? (source as JobsQuery['source']) : undefined,
        state: state in JOB_STATES ? (state as JobsQuery['state']) : undefined,
        period,
        page: page + 1,
        pageSize: 25,
      },
      system,
    );
    const unavailable = new Set(next.sources.filter((s) => !s.available).map((s) => s.source));
    if (lastPage.current?.key === key) {
      const retained = lastPage.current.data.items.filter(
        (job) => unavailable.has(job.source) && !next.items.some((j) => j.key === job.key),
      );
      next.items = [...next.items, ...retained];
    }
    lastPage.current = { key, data: next };
    return next;
  }, [workspaceId, system, view, source, state, page, period]);
  const { data, error, refresh } = useVisiblePolling(load);
  function change(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page' && key !== 'job') next.delete('page');
    if (key === 'scope') {
      next.delete('source');
      next.delete('job');
    }
    setParams(next);
  }
  return (
    <>
      <PageHeader
        title={sectionById('jobs').label}
        description="Прогресс, ожидание и результаты фоновых работ. Обновление каждые 15 секунд."
        action={
          <Button variant="outlined" onClick={refresh}>
            Обновить статус
          </Button>
        }
      />
      <Stack gap={2}>
        {admin && (
          <Tabs value={system ? 'system' : 'workspace'} onChange={(_, v) => change('scope', v)}>
            <Tab value="workspace" label="Текущий workspace" />
            <Tab value="system" label="Системные" />
          </Tabs>
        )}
        <Tabs value={view} onChange={(_, v) => change('view', v)}>
          <Tab value="active" label="Активные" />
          <Tab value="attention" label="Требуют внимания" />
          <Tab value="history" label="История" />
        </Tabs>
        <Stack direction="row" gap={2} flexWrap="wrap">
          <TextField
            select
            label="Источник"
            value={source}
            onChange={(e) => change('source', e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Все источники</MenuItem>
            {Object.entries(JOB_SOURCES)
              .filter(([s]) => (system ? s === 'n8n' : s !== 'n8n'))
              .map(([s, label]) => (
                <MenuItem key={s} value={s}>
                  {label}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            select
            label="Состояние"
            value={state}
            onChange={(e) => change('state', e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Все состояния</MenuItem>
            {Object.entries(JOB_STATES).map(([s, label]) => (
              <MenuItem key={s} value={s}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          {view === 'history' && (
            <TextField
              select
              label="Период"
              value={period}
              onChange={(e) => change('period', e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="30">Последние 30 дней</MenuItem>
              <MenuItem value="all">Вся доступная история</MenuItem>
            </TextField>
          )}
        </Stack>
        {error && (
          <Alert severity="warning">
            Не удалось обновить работы: {error}
            {data ? '. Показаны последние полученные сведения.' : ''}
          </Alert>
        )}
        {!data && !error && <LinearProgress />}
        {data && (
          <>
            {data.queueAdmission && (
              <QueueAdmissionStatus
                profilesEnabled={admin}
                value={error ? { ...data.queueAdmission, freshness: 'stale' } : data.queueAdmission}
                budget={
                  error && data.resourceBudget
                    ? { ...data.resourceBudget, freshness: 'stale' }
                    : data.resourceBudget
                }
              />
            )}
            <Typography fontSize={12} color="text.secondary">
              Статус получен {date(data.observedAt)}
              {error ? ' · данные устарели' : ''}
            </Typography>
            {data.sources
              .filter((s) => !s.available)
              .map((s) => (
                <Alert severity="warning" key={s.source}>
                  {JOB_SOURCES[s.source]}: {s.error}. Список неполный; ранее полученные строки этого
                  источника могут быть устаревшими.
                </Alert>
              ))}
            {data.items.map((job) => (
              <Card key={job.key} sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <JobSummary job={job} />
                  </Box>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Button onClick={() => change('job', job.key)}>Подробнее</Button>
                    <Button component={Link} to={job.href}>
                      Открыть раздел
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            ))}
            {!data.items.length && (
              <Typography color="text.secondary">Нет работ по выбранным условиям.</Typography>
            )}
            <TablePagination
              {...paginationLabels}
              component="div"
              count={data.sources.some((s) => !s.available) ? -1 : data.total}
              page={page}
              rowsPerPage={25}
              rowsPerPageOptions={[25]}
              onPageChange={(_, p) => change('page', String(p))}
            />
            <Accordion>
              <AccordionSummary>Источники, обработчики и доступная история</AccordionSummary>
              <AccordionDetails>
                <Stack gap={2}>
                  {data.sources.map((s) => (
                    <Box key={s.source}>
                      <Typography fontWeight={700}>{JOB_SOURCES[s.source]}</Typography>
                      <Typography fontSize={13}>
                        {s.worker}
                        {s.lastSeenAt ? ' · наблюдение ' + date(s.lastSeenAt) : ''}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {s.history}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
      {jobKey && (
        <JobDialog
          key={jobKey + ':' + system}
          workspaceId={workspaceId}
          jobKey={jobKey}
          system={system}
          admin={admin}
          onClose={() => change('job', '')}
          onOpen={(key) => change('job', key)}
        />
      )}
    </>
  );
}
